/**
 * Safe diagnostics for subscribe / PayPal checkout (no secrets in console).
 * Prefix all logs with [ema-checkout] for filtering.
 */

const LOG_PREFIX = '[ema-checkout]'

/** First / last few chars only — never log full tokens. */
export function maskSecret(value: string | undefined, head = 8, tail = 4): string {
  if (!value || value.length === 0) return '(empty)'
  if (value.length <= head + tail) return `(len=${value.length}, redacted)`
  return `${value.slice(0, head)}…${value.slice(-tail)} (len=${value.length})`
}

export function classifyCheckoutRedirectUrl(url: string): {
  kind: 'paypal_hosted' | 'same_origin_account' | 'cross_origin_account' | 'unknown'
  hostname: string
  pathname: string
  note: string
} {
  try {
    const u = new URL(url)
    const host = u.hostname.toLowerCase()
    const isPayPal =
      host.includes('paypal.com') ||
      host.includes('paypalobjects.com')
    if (isPayPal) {
      return {
        kind: 'paypal_hosted',
        hostname: u.hostname,
        pathname: u.pathname,
        note: 'Browser navigates to PayPal-hosted checkout',
      }
    }
    const origin = typeof window !== 'undefined' ? window.location.origin : ''
    const sameOrigin = origin && url.startsWith(origin)
    const isAccount = u.pathname.includes('/account')
    if (sameOrigin && isAccount) {
      return {
        kind: 'same_origin_account',
        hostname: u.hostname,
        pathname: u.pathname,
        note: 'Direct redirect to this site /account — no PayPal page (plan change, downgrade, or misconfiguration)',
      }
    }
    if (isAccount) {
      return {
        kind: 'cross_origin_account',
        hostname: u.hostname,
        pathname: u.pathname,
        note: 'Redirect to /account on another host — check WEB_APP_URL / PAYPAL_CHECKOUT_RETURN_BASE',
      }
    }
    return {
      kind: 'unknown',
      hostname: u.hostname,
      pathname: u.pathname,
      note: 'Unexpected redirect target',
    }
  } catch {
    return {
      kind: 'unknown',
      hostname: '',
      pathname: '',
      note: `Invalid URL string (first 80 chars): ${String(url).slice(0, 80)}`,
    }
  }
}

/** Snapshot of Vite env baked in at build time (S3 deploy = set vars before npm run build, not Heroku). */
export function getBillingBuildSnapshot() {
  const raw = import.meta.env.VITE_PAYPAL_CLIENT_ID
  const hasToken = typeof raw === 'string' && raw.trim().length > 0
  return {
    mode: import.meta.env.MODE,
    dev: import.meta.env.DEV,
    prod: import.meta.env.PROD,
    VITE_SERVER_URL: import.meta.env.VITE_SERVER_URL || '(unset — using relative /api/v1)',
    VITE_FRONTEND_URL: import.meta.env.VITE_FRONTEND_URL || '(unset)',
    VITE_PAYPAL_CLIENT_ID_set: hasToken,
    VITE_PAYPAL_CLIENT_ID_preview: hasToken ? maskSecret(raw.trim()) : '(not set at build)',
    documentOrigin: typeof window !== 'undefined' ? window.location.origin : '(ssr)',
  }
}

export function logCheckout(tag: string, payload: Record<string, unknown>) {
  console.info(LOG_PREFIX, tag, payload)
}
